import type { ExtendedChatMessage } from "@/components/aios-command/chat/chat-types";
import type { ChatMessage, MessageContentItem } from "@/hooks/use-command-messages";
import type { CommandAgent, CommandProject, CommandSession } from "@/types/command-agent";
import type { CommandV2PresetDisplay } from "@/components/command-team-v2/types";
import type {
    ProtoChatMessage,
    ProtoChatSession,
    ProtoCommandAgent,
    ProtoPreset,
    ProtoProject,
} from "@/components/command-team-v2/prototype/types";
import { getExpiredIntegrationNamesForAgent } from "@/utils/command-agent-expired-integrations";
import type { IntegrationLookupRow } from "@/utils/command-agent-expired-integrations";
import { apiVisibilityToProto } from "@/types/visibility";

function getStructuredItemText(item: MessageContentItem): string {
    if (typeof item.text === "string") return item.text;
    const content = (item as { content?: string }).content;
    if (typeof content === "string") return content;
    return "";
}

function extractDisplayContent(message: ChatMessage | ExtendedChatMessage): string {
    if (typeof message.content === "string" && message.content.trim()) {
        return message.content;
    }

    const structured = (message as ChatMessage & { structuredContent?: MessageContentItem[] })
        .structuredContent;
    if (Array.isArray(structured) && structured.length > 0) {
        const textTypes = new Set(["text", "output_text", "assistant_text", "message"]);
        const parts = structured
            .filter((item) => {
                const type = String(item.type ?? "").toLowerCase();
                return textTypes.has(type) || !type;
            })
            .map(getStructuredItemText)
            .filter(Boolean);
        if (parts.length > 0) return parts.join("\n\n");
    }

    const segments = (message as ExtendedChatMessage).contentSegments;
    if (segments?.length) {
        const text = segments
            .filter((segment) => segment.type === "text" && segment.content?.trim())
            .map((segment) => segment.content!)
            .join("\n\n");
        if (text.trim()) return text;
    }

    return typeof message.content === "string" ? message.content : "";
}

export function mapSessionToProto(
    session: CommandSession,
    messages: ProtoChatMessage[] = []
): ProtoChatSession {
    const createdAt = session.created_at ?? new Date().toISOString();
    return {
        id: session.id,
        title: session.title?.trim() || "Untitled chat",
        projectId: session.project_id ?? null,
        agentId: session.agent_id ?? null,
        pinned: session.pinned ?? false,
        messages,
        createdAt,
        updatedAt: session.updated_at ?? createdAt,
    };
}

export function mapProjectToProto(project: CommandProject): ProtoProject {
    return {
        id: project.id,
        name: project.name,
        description: project.description ?? undefined,
        instructions: project.instructions ?? undefined,
        visibility: project.visibility === "TEAM" ? "team" : "personal",
        agentId: project.agent_id ?? null,
    };
}

export function mapAgentToProto(
    agent: CommandAgent,
    options?: { isActive?: boolean; integrationLookup?: IntegrationLookupRow[] }
): ProtoCommandAgent {
    return {
        id: agent.id,
        name: agent.name,
        role: agent.role ?? "",
        description: agent.description ?? "",
        persona: agent.persona ?? "",
        restrictions: agent.restrictions ?? "",
        visibility: apiVisibilityToProto(agent.visibility ?? "PERSONAL"),
        isActive: options?.isActive ?? agent.is_active !== false,
        isDefault: agent.is_system ?? false,
        createdAt: agent.created_at,
        llmConfig: {
            model: agent.llm_model ?? "",
            provider: agent.llm_provider ?? "",
            temperature: agent.llm_temperature ?? 0.2,
            maxTokens: agent.llm_max_tokens ?? 8192,
            timeoutSec: agent.llm_timeout ?? 120,
        },
        connectedApps: agent.app_integrations?.map((item) => item.name) ?? [],
        customApps: agent.custom_apps?.map((item) => item.name) ?? [],
        knowledgeBases: [],
        creditLimit: { amount: 10000, period: "week" },
        appIssues: getExpiredIntegrationNamesForAgent(agent, options?.integrationLookup),
    };
}

export function mapPresetToProto(preset: CommandV2PresetDisplay): ProtoPreset {
    return {
        id: preset.id,
        title: preset.title,
        description: preset.description,
        promptText: preset.promptText,
        category: preset.category,
        favourite: preset.favourite,
        order: preset.order,
    };
}

function extractStreamError(message: ChatMessage | ExtendedChatMessage): string | undefined {
    const extended = message as ExtendedChatMessage;
    if (extended.streamError?.trim()) return extended.streamError.trim();
    const fromSegment = extended.contentSegments
        ?.find((segment) => segment.type === "error")
        ?.content?.trim();
    return fromSegment || undefined;
}

export function mapMessageToProto(
    message: ChatMessage | ExtendedChatMessage
): ProtoChatMessage {
    const pending =
        "isStreaming" in message
            ? Boolean(message.isStreaming)
            : message.status === "GENERATING";

    return {
        id: message.id,
        role: message.role,
        content: extractDisplayContent(message),
        createdAt:
            message.timestamp instanceof Date
                ? message.timestamp.toISOString()
                : new Date().toISOString(),
        pending,
        suggestions: message.suggestions,
        streamError: extractStreamError(message),
        contentSegments: (message as ExtendedChatMessage).contentSegments,
        tools: (message as ExtendedChatMessage).tools,
        attachedFiles: message.attachedFiles?.map((file) => ({
            name: file.file_name,
            size: file.file_size ?? 0,
        })),
        contextUsage: message.contextUsage,
    };
}
